import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useAree, useSessione } from '../servizi/sessione.tsx'
import { Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, EtichettaArea, Modale, Riquadro, Scelta, Tabella } from '../componenti/base.tsx'
import { IconaLucchetto } from '../componenti/Icone.tsx'
import { GruppoCaselle, useFuocoSulPrimoErrore, vociDi } from '../componenti/persona-campi.tsx'
import {
  CampiAbitativa, CampiCondizione, CampiConoscenza, CampiDatiAnagrafici, CampiFormazione, CampoSede,
  LetturaAbitativa, LetturaCondizione, LetturaConoscenza, LetturaDatiAnagrafici, LetturaFormazione, giornoLeggibile,
} from './SchedaSezioni.tsx'
import {
  MOTIVI_PROPOSTA, NOMI_MESI, campoDiErrore, condizionePerOperatore, corpiUguali, corpoAbitativa, corpoAnagrafica,
  corpoCondizione, corpoFormazione, corpoServizi, differenze, etaAllaData, giornoIso, nessunErrore, oggiLocale,
  scriveCategoria, scriveVulnerabilita, statoDaScheda, tipoOperatore, verificaAbitativa, verificaAnagrafica, verificaCategorie,
  verificaCondizione, verificaFormazione, verificaServizi, verificaVulnerabilita,
  type CataloghiRegole, type Errori, type SchedaLetta, type StatoAnagrafica, type VulnerabilitaLetta,
} from '../servizi/regole-anagrafica.ts'
import { AltriCodici } from './scheda/AltriCodici.tsx'
import { mancaObbligatorio } from '../servizi/dati-mancanti.ts'

/**
 * Anagrafica della scheda (Scheda utente Rev05): intestazione, poi le sezioni del modulo in
 * lettura e, per chi ha il permesso sulla scheda, in modifica una alla volta. In coda il profilo
 * di vulnerabilita' (Vulnus) e la categoria di utenti con le proposte del sistema da confermare.
 *
 * I permessi arrivano dalla scheda (`puo`), calcolati dall'API sul perimetro reale: l'interfaccia
 * li usa per non mostrare comandi inutili, la decisione resta dell'API.
 */

type Ricarica = () => Promise<void>

/** Errore dell'API riportato sul campo, oppure in testa alla sezione se non riguarda un campo. */
function erroreSulCampo(e: unknown, campiDellaSezione: (campo: string) => boolean) {
  const campo = e instanceof ErroreApi ? campoDiErrore(e) : null
  if (campo && campiDellaSezione(campo.campo)) {
    return { errori: { [campo.campo]: campo.messaggio } as Errori, messaggio: 'Correggere i campi segnalati.' }
  }
  if (e instanceof ErroreApi && e.stato === 404) {
    return { errori: {}, messaggio: 'L’operazione non rientra negli incarichi in corso su questa scheda.' }
  }
  return { errori: {}, messaggio: e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.' }
}

/** Modulo di una sezione in modifica: invio senza la validazione del browser, fuoco sul primo errore. */
function ModuloSezione({
  nota, errore, inCorso, tentativo, annulla, invia, children,
}: {
  nota?: ReactNode; errore: string | null; inCorso: boolean; tentativo: number
  annulla: () => void; invia: () => Promise<void>; children: ReactNode
}) {
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)
  return (
    <form ref={radice} noValidate onSubmit={(e) => { e.preventDefault(); void invia() }}>
      {nota && <p className="persona-nota-sezione">{nota}</p>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {children}
      <div className="persona-comandi">
        <Bottone variante="secondario" onClick={annulla} disabled={inCorso}>Annulla</Bottone>
        <Bottone tipo="submit" variante="primario" inCorso={inCorso}>Salva</Bottone>
      </div>
    </form>
  )
}

/** Stato comune dei moduli di sezione: errori per campo, messaggio in testa, invio in corso. */
function useInvio() {
  const [errori, setErrori] = useState<Errori>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  const segnala = (nuovi: Errori, messaggio: string) => { setErrori(nuovi); setErrore(messaggio); setTentativo((t) => t + 1) }
  const pulisci = () => { setErrori({}); setErrore(null) }
  return { errori, errore, inCorso, setInCorso, tentativo, segnala, pulisci }
}

const cataloghiDi = (cataloghi: unknown) => (cataloghi ?? {}) as CataloghiRegole

// --- intestazione (D-05, D-10) ---------------------------------------------------------------

function VoceIntestazione({ nome, larga = false, mancante = false, children }: {
  nome: string; larga?: boolean
  /** Dato obbligatorio che manca (Osservazioni 4): tono di attenzione, come nei dati anagrafici. */
  mancante?: boolean
  children: ReactNode
}) {
  const classi = ['persona-intestazione-voce', larga && 'persona-intestazione-larga', mancante && 'dato-mancante']
  return (
    <div className={classi.filter(Boolean).join(' ')}>
      <dt>{nome}</dt>
      <dd>
        {children ?? <span className={mancante ? undefined : 'tenue'}>—</span>}
        {mancante && <span className="dato-mancante-nota">Obbligatorio: da completare</span>}
      </dd>
    </div>
  )
}

/**
 * Intestazione della Scheda Rev05: Sede Servizio (sede di riferimento), progetti e aree ricavati
 * dai percorsi aperti e chiusi, anno e mese di creazione con chi l'ha inserita (Osservazioni 4 del
 * 23/10/2026), numero e codice dell'archivio precedente. Si modifica solo la sede: il resto non si
 * inserisce.
 */
export function IntestazioneScheda({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: Ricarica }) {
  const aree = useAree()
  const [inModifica, setInModifica] = useState(false)
  const i = scheda.intestazione
  const modificabile = scheda.puo.modificare && !scheda.mascherata
  return (
    <Riquadro
      titolo="Intestazione della scheda"
      azioni={modificabile && !inModifica
        ? <Bottone variante="secondario" dimensione="piccolo" onClick={() => setInModifica(true)}>Cambia sede</Bottone>
        : undefined}
    >
      {inModifica
        ? <ModificaAnagrafica scheda={scheda} parte="intestazione" chiudi={() => setInModifica(false)} ricarica={ricarica} />
        : (
          <dl className="persona-intestazione">
            <VoceIntestazione nome="Sede Servizio" mancante={mancaObbligatorio(scheda.dati_mancanti, 'sede_riferimento')}>
              {i.sede?.nome ?? null}
            </VoceIntestazione>
            <VoceIntestazione nome="Progetto" larga>
              {i.progetti.length
                ? i.progetti.map((p) => `${p.codice_cidis ? `${p.codice_cidis} · ` : ''}${p.titolo}`).join('; ')
                : null}
            </VoceIntestazione>
            <VoceIntestazione nome="Area" larga>
              {i.aree.length
                ? (
                  <span className="riga-etichette">
                    {i.aree.map((a) => (
                      <span key={a.codice} className="dato-con-etichetta">
                        <EtichettaArea codice={a.codice} colore={aree.coloreDi(a.codice)} />
                        {a.nome}
                        {!a.aperta && <span className="tenue piccolo">(percorsi chiusi)</span>}
                      </span>
                    ))}
                  </span>
                )
                : null}
            </VoceIntestazione>
            {/* Chi l'ha inserita sta sotto l'anno: per le schede migrate e' l'autore del vecchio archivio. */}
            <VoceIntestazione nome="Anno">
              {i.anno}
              <span className="persona-inserita-da">Inserita da {i.inserita_da ?? '—'}</span>
            </VoceIntestazione>
            <VoceIntestazione nome="Mese">{NOMI_MESI[i.mese - 1] ?? i.mese}</VoceIntestazione>
            <VoceIntestazione nome="Scheda SUS n."><span className="mono">{i.numero_scheda}</span></VoceIntestazione>
            {i.codice_storico && (
              <VoceIntestazione nome="Archivio precedente"><span className="mono">{i.codice_storico}</span></VoceIntestazione>
            )}
            {/* «Unisci schede» (473): le schede unite a questa restano ritrovabili per numero e codice. */}
            {!!i.schede_unite?.length && (
              <VoceIntestazione nome="Schede unite" larga>
                {i.schede_unite.map((u, n) => (
                  <span key={u.numero_scheda} className="mono">
                    {n > 0 && '; '}
                    n. {u.numero_scheda}{u.codice_storico ? ` (archivio precedente ${u.codice_storico})` : ''}
                    {u.unita_il ? `, unita il ${new Date(u.unita_il).toLocaleDateString('it-IT')}` : ''}
                  </span>
                ))}
              </VoceIntestazione>
            )}
            <VoceIntestazione nome="Profilo utenti">{scheda.tipo_nome}</VoceIntestazione>
          </dl>
        )}
    </Riquadro>
  )
}

// --- sezioni del modulo ----------------------------------------------------------------------

type Parte = 'conoscenza' | 'anagrafici' | 'condizione' | 'formazione' | 'abitativa'

export function AnagraficaScheda({ scheda, ricarica, apriAnagrafici = false, aperti }: {
  scheda: SchedaLetta; ricarica: Ricarica
  /** «Modifica la scheda» dell'intestazione (Osservazioni 4): apre i dati anagrafici in modifica. */
  apriAnagrafici?: boolean
  /** La richiesta e' servita: chi la fa la azzera. */
  aperti?: () => void
}) {
  const [aperta, setAperta] = useState<Parte | null>(null)
  const operatore = tipoOperatore(scheda.tipo)
  const puo = scheda.puo
  const chiudi = () => setAperta(null)

  useEffect(() => {
    if (!apriAnagrafici) return
    // Una sezione gia' in modifica non si chiude da qui: si perderebbe quello che si sta scrivendo.
    setAperta((attuale) => attuale ?? (puo.modificare && !scheda.mascherata ? 'anagrafici' : null))
    // Senza annullarlo allo smontaggio: l'azzeramento qui sotto ridisegna, e l'effetto ripulito
    // perderebbe lo scorrimento.
    window.setTimeout(() => document.getElementById('scheda-anagrafici')?.scrollIntoView({ block: 'start' }), 50)
    aperti?.()
    // Solo alla richiesta: i permessi non cambiano mentre la scheda e' aperta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apriAnagrafici])

  /** Una sezione alla volta in modifica: le altre restano in lettura con il comando disattivato. */
  const comando = (parte: Parte, ammesso: boolean) => (ammesso && !scheda.mascherata && aperta !== parte
    ? (
      <Bottone variante="secondario" dimensione="piccolo" disabled={aperta !== null} onClick={() => setAperta(parte)}>
        Modifica
      </Bottone>
    )
    : undefined)

  return (
    <>
      <Riquadro titolo="Come è venuto a conoscenza del Servizio" azioni={comando('conoscenza', puo.modificare)}>
        {aperta === 'conoscenza'
          ? <ModificaAnagrafica scheda={scheda} parte="conoscenza" chiudi={chiudi} ricarica={ricarica} />
          : <LetturaConoscenza scheda={scheda} />}
      </Riquadro>

      {/* Ancora per «Completa la scheda» (460). */}
      <div id="scheda-anagrafici" className="persona-ancora">
        <Riquadro titolo="Dati anagrafici" azioni={comando('anagrafici', puo.modificare)}>
          {aperta === 'anagrafici'
            ? <ModificaAnagrafica scheda={scheda} parte="anagrafici" chiudi={chiudi} ricarica={ricarica} />
            : <LetturaDatiAnagrafici scheda={scheda} />}
        </Riquadro>
      </div>

      {/* 481: sulla scheda con dati anonimizzati gli altri codici non si registrano. */}
      {!scheda.mascherata && !scheda.dati_anonimizzati && (
        <AltriCodici personaId={scheda.id} puoModificare={puo.modificare} vedeValori={puo.vedereIdentificativi} />
      )}

      {operatore ? (
        <Avviso tipo="informazione">
          Per chi rappresenta un ente o fa parte del personale CIDIS non si registrano condizione giuridica,
          formazione – lavoro e condizione abitativa e familiare.
        </Avviso>
      ) : (
        <>
          <Riquadro titolo="Condizione giuridica"
                    azioni={comando('condizione', puo.modificareParticolari && scheda.condizione_giuridica !== undefined)}>
            {aperta === 'condizione'
              ? <ModificaCondizione scheda={scheda} chiudi={chiudi} ricarica={ricarica} />
              : <LetturaCondizione scheda={scheda} />}
          </Riquadro>

          <Riquadro titolo="Formazione – Lavoro"
                    azioni={comando('formazione', puo.modificare && scheda.formazione_lavoro !== undefined)}>
            {aperta === 'formazione'
              ? <ModificaFormazione scheda={scheda} chiudi={chiudi} ricarica={ricarica} />
              : <LetturaFormazione scheda={scheda} />}
          </Riquadro>

          <Riquadro titolo="Condizione abitativa e familiare"
                    azioni={comando('abitativa', puo.modificare && scheda.condizione_abitativa !== undefined)}>
            {aperta === 'abitativa'
              ? <ModificaAbitativa scheda={scheda} chiudi={chiudi} ricarica={ricarica} />
              : <LetturaAbitativa scheda={scheda} />}
          </Riquadro>
        </>
      )}

      {scheda.vulnerabilita !== undefined && <ProfiloVulnerabilita scheda={scheda} ricarica={ricarica} />}
      <CategorieUtenti scheda={scheda} ricarica={ricarica} />
    </>
  )
}

/** Campi di StatoAnagrafica, e quindi errori, che appartengono a ogni parte modificabile. */
const CAMPI_PARTE = {
  intestazione: (campo: string) => campo === 'sede_riferimento',
  conoscenza: (campo: string) => campo === 'canali_conoscenza' || campo === 'canale_specifica',
  anagrafici: (campo: string) => !['sede_riferimento', 'canali_conoscenza', 'canale_specifica'].includes(campo),
}

/**
 * Intestazione, canali di conoscenza e dati anagrafici: tutti con PATCH /persone/:id, inviando
 * solo i campi cambiati. Le verifiche valgono per la parte in modifica, cosi' un dato del
 * vecchio archivio in un'altra parte non blocca il salvataggio.
 */
function ModificaAnagrafica({ scheda, parte, chiudi, ricarica }: {
  scheda: SchedaLetta; parte: keyof typeof CAMPI_PARTE; chiudi: () => void; ricarica: Ricarica
}) {
  const { cataloghi } = useSessione()
  const cat = cataloghiDi(cataloghi)
  const iniziale = useMemo(() => statoDaScheda(scheda).anagrafica, [scheda])
  const [stato, setStato] = useState(iniziale)
  const invio = useInvio()
  const cambia = (p: Partial<StatoAnagrafica>) => setStato((s) => ({ ...s, ...p }))
  const dellaParte = CAMPI_PARTE[parte]
  const condizione = condizionePerOperatore(scheda)
  const diventaOperatore = !tipoOperatore(iniziale.tipo) && tipoOperatore(stato.tipo)

  async function invia() {
    invio.pulisci()
    const verificati = Object.fromEntries(Object.entries(
      verificaAnagrafica(stato, cat, { sedeObbligatoria: false, oggi: oggiLocale(), dataIniziale: iniziale.data_nascita }),
    ).filter(([campo]) => dellaParte(campo)))
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    if (diventaOperatore && condizione.bloccato) {
      invio.segnala({ tipo: 'Il passaggio a «Operatore» lo esegue chi può modificare i dati particolari.' },
        'Con gli incarichi in corso la scheda non passa a «Operatore».')
      return
    }

    let corpo = differenze(corpoAnagrafica(stato, cat), corpoAnagrafica(iniziale, cat))
    // D-07: chi rappresenta un ente non ha condizione giuridica; passando a operatore si toglie.
    if (diventaOperatore && condizione.presente) {
      corpo = { ...corpo, condizione_giuridica: null, motivo_permesso: null }
    }
    if (!Object.keys(corpo).length) { chiudi(); return }

    invio.setInCorso(true)
    try {
      await api.patch(`/persone/${scheda.id}`, corpo)
      await ricarica()
      chiudi()
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, dellaParte)
      invio.segnala(errori, parte === 'intestazione' && e instanceof ErroreApi && e.stato === 404
        ? 'La sede scelta non rientra negli incarichi in corso.' : messaggio)
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <ModuloSezione errore={invio.errore} inCorso={invio.inCorso} tentativo={invio.tentativo} annulla={chiudi} invia={invia}>
      {parte === 'intestazione' && (
        <div className="griglia-modulo">
          <CampoSede valore={stato.sede_riferimento} obbligatorio={false} errore={invio.errori.sede_riferimento}
                     cambia={(sede) => cambia({ sede_riferimento: sede })} />
        </div>
      )}
      {parte === 'conoscenza' && <CampiConoscenza stato={stato} cambia={cambia} errori={invio.errori} />}
      {parte === 'anagrafici' && (
        <>
          {diventaOperatore && (
            <Avviso tipo="attenzione">
              Passando a «{stato.tipo === 'operatore_cidis' ? 'CIDIS Dipendenti e Collaboratori' : 'Operatore'}» serve
              l’ente oppure la sede CIDIS di appartenenza.
              {condizione.bloccato
                ? ' Il passaggio lo esegue chi può modificare i dati particolari, perché la condizione giuridica registrata va tolta.'
                : condizione.presente ? ' La condizione giuridica registrata si toglie.' : ''}
              {' '}Le rilevazioni di formazione – lavoro e di condizione abitativa restano nello storico e
              non si mostrano.
            </Avviso>
          )}
          {/* 481: togliendo «Dati anonimizzati» si completano nome e cognome. */}
          {iniziale.dati_anonimizzati && !stato.dati_anonimizzati && (
            <Avviso tipo="attenzione">
              Scrivi nome e cognome per intero e, senza codice fiscale, il motivo. Da qui valgono le regole normali: i
              nuovi interventi chiedono i consensi 1 e 2.
            </Avviso>
          )}
          <CampiDatiAnagrafici stato={stato} cambia={cambia} errori={invio.errori} conTipo
                               vedeIdentificativi={scheda.puo.vedereIdentificativi}
                               anonimizzazione={{ ostacoli: scheda.anonimizzazione?.ostacoli ?? [],
                                                  registrata: !!scheda.dati_anonimizzati }} />
        </>
      )}
    </ModuloSezione>
  )
}

const CAMPI_CONDIZIONE = ['condizione_giuridica', 'motivo_permesso', 'permesso_scadenza']

function ModificaCondizione({ scheda, chiudi, ricarica }: { scheda: SchedaLetta; chiudi: () => void; ricarica: Ricarica }) {
  const { cataloghi } = useSessione()
  const cat = cataloghiDi(cataloghi)
  const iniziale = useMemo(() => statoDaScheda(scheda).condizione, [scheda])
  const [stato, setStato] = useState(iniziale)
  const invio = useInvio()

  async function invia() {
    invio.pulisci()
    const verificati = verificaCondizione(stato, cat, iniziale)
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    const corpo = differenze(corpoCondizione(stato), corpoCondizione(iniziale))
    if (!Object.keys(corpo).length) { chiudi(); return }
    invio.setInCorso(true)
    try {
      await api.patch(`/persone/${scheda.id}`, corpo)
      await ricarica()
      chiudi()
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, (c) => CAMPI_CONDIZIONE.includes(c))
      invio.segnala(errori, messaggio)
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <ModuloSezione errore={invio.errore} inCorso={invio.inCorso} tentativo={invio.tentativo} annulla={chiudi} invia={invia}>
      <CampiCondizione stato={stato} cambia={(p) => setStato((s) => ({ ...s, ...p }))} errori={invio.errori}
                       anonimizzati={!!scheda.dati_anonimizzati} />
    </ModuloSezione>
  )
}

const CAMPI_SERVIZI = ['voci', 'motivo', 'motivo_specifica']

/**
 * Formazione – Lavoro: la rilevazione cambiata diventa una rilevazione nuova (le precedenti
 * restano nello storico); i servizi bancari si sostituiscono come insieme. Se la rilevazione
 * riesce e i servizi no, la scheda si ricarica prima di segnalare l'errore: al nuovo invio la
 * rilevazione risulta uguale e non si registra due volte.
 */
function ModificaFormazione({ scheda, chiudi, ricarica }: { scheda: SchedaLetta; chiudi: () => void; ricarica: Ricarica }) {
  const { cataloghi } = useSessione()
  const cat = cataloghiDi(cataloghi)
  const iniziali = useMemo(() => statoDaScheda(scheda), [scheda])
  const [stato, setStato] = useState(iniziali.formazione)
  const [servizi, setServizi] = useState(iniziali.servizi)
  const invio = useInvio()
  const [erroriServizi, setErroriServizi] = useState<Errori>({})

  async function invia() {
    invio.pulisci(); setErroriServizi({})
    const verificati = verificaFormazione(stato, cat)
    const verificatiServizi = verificaServizi(servizi, cat)
    if (!nessunErrore(verificati) || !nessunErrore(verificatiServizi)) {
      setErroriServizi(verificatiServizi)
      invio.segnala(verificati, 'Correggere i campi segnalati.')
      return
    }
    const lavoro = corpoFormazione(stato)
    const insieme = corpoServizi(servizi, cat)
    const nuovaRilevazione = !corpiUguali(lavoro, corpoFormazione(iniziali.formazione))
    const serviziCambiati = !corpiUguali(insieme, corpoServizi(iniziali.servizi, cat))
    if (!nuovaRilevazione && !serviziCambiati) { chiudi(); return }

    invio.setInCorso(true)
    let registrata = false
    try {
      if (nuovaRilevazione) {
        await api.post(`/persone/${scheda.id}/situazioni/lavorativa`, lavoro)
        registrata = true
      }
      if (serviziCambiati) await api.put(`/persone/${scheda.id}/servizi-bancari`, insieme)
      await ricarica()
      chiudi()
    } catch (e) {
      if (registrata) await ricarica().catch(() => undefined)
      const campo = e instanceof ErroreApi ? campoDiErrore(e) : null
      if (campo && CAMPI_SERVIZI.includes(campo.campo)) {
        setErroriServizi({ [campo.campo]: campo.messaggio })
        invio.segnala({}, 'Correggere i campi segnalati.')
      } else {
        const { errori, messaggio } = erroreSulCampo(e, (c) => !CAMPI_SERVIZI.includes(c))
        invio.segnala(errori, messaggio)
      }
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <ModuloSezione errore={invio.errore} inCorso={invio.inCorso} tentativo={invio.tentativo} annulla={chiudi} invia={invia}
                   nota="Il salvataggio registra una nuova rilevazione con la data di oggi; le precedenti restano nello storico.">
      <CampiFormazione stato={stato} cambia={(p) => setStato((s) => ({ ...s, ...p }))} errori={invio.errori}
                       servizi={servizi} cambiaServizi={(p) => setServizi((s) => ({ ...s, ...p }))}
                       erroriServizi={erroriServizi} />
    </ModuloSezione>
  )
}

function ModificaAbitativa({ scheda, chiudi, ricarica }: { scheda: SchedaLetta; chiudi: () => void; ricarica: Ricarica }) {
  const { cataloghi } = useSessione()
  const cat = cataloghiDi(cataloghi)
  const iniziale = useMemo(() => statoDaScheda(scheda).abitativa, [scheda])
  const [stato, setStato] = useState(iniziale)
  const invio = useInvio()

  async function invia() {
    invio.pulisci()
    const verificati = verificaAbitativa(stato, cat)
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    const corpo = corpoAbitativa(stato)
    if (corpiUguali(corpo, corpoAbitativa(iniziale))) { chiudi(); return }
    invio.setInCorso(true)
    try {
      await api.post(`/persone/${scheda.id}/situazioni/abitativa`, corpo)
      await ricarica()
      chiudi()
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, () => true)
      invio.segnala(errori, messaggio)
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <ModuloSezione errore={invio.errore} inCorso={invio.inCorso} tentativo={invio.tentativo} annulla={chiudi} invia={invia}
                   nota="Il salvataggio registra una nuova rilevazione con la data di oggi; le precedenti restano nello storico.">
      <CampiAbitativa stato={stato} cambia={(p) => setStato((s) => ({ ...s, ...p }))} errori={invio.errori} />
    </ModuloSezione>
  )
}

// --- vulnus: profilo di vulnerabilita' (D-21) ------------------------------------------------

/**
 * Vulnus: una o piu' voci del profilo di vulnerabilita', ciascuna con la data di rilevazione e,
 * quando termina, di conclusione. Le voci riservate (tratta, sfruttamento, discriminazione,
 * torture) si registrano con il permesso di scrivere e di leggere l'area riservata della scheda;
 * l'API restituisce solo le voci che l'utente puo' vedere. `aperte` contiene quindi solo voci
 * visibili: proporre una voce riservata a chi non la legge farebbe rispondere l'API 409 se gia'
 * registrata da altri, e lo rivelerebbe (per questo l'API risponde 404).
 */
function ProfiloVulnerabilita({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: Ricarica }) {
  const { cataloghi } = useSessione()
  const catalogo = vociDi(cataloghi, 'vulnerabilita')
  const rilevazioni = scheda.vulnerabilita ?? []
  const oggi = oggiLocale()
  const scrive = (classe: string | undefined, visibile: boolean) =>
    !scheda.mascherata && scriveVulnerabilita(scheda.puo, classe, visibile)
  const aperte = new Set(rilevazioni.filter((r) => !r.conclusa_il).map((r) => r.vulnerabilita))
  const aggiungibili = catalogo.filter((v) => scrive(v.classe_dato, false) && !aperte.has(v.codice))

  const vuota = () => ({ codice: '', rilevata_il: oggi, conclusa_il: '' })
  const [nuova, setNuova] = useState(vuota)
  const [inModifica, setInModifica] = useState<VulnerabilitaLetta | null>(null)
  const invio = useInvio()
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, invio.tentativo)

  async function aggiungi() {
    invio.pulisci()
    const verificati = verificaVulnerabilita(nuova, oggi)
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    invio.setInCorso(true)
    try {
      await api.post(`/persone/${scheda.id}/vulnerabilita`, {
        codice: nuova.codice, rilevata_il: nuova.rilevata_il, conclusa_il: nuova.conclusa_il || null,
      })
      setNuova(vuota())
      await ricarica()
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, (c) => ['codice', 'rilevata_il', 'conclusa_il'].includes(c))
      invio.segnala(errori, messaggio)
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <Riquadro titolo="Vulnus – profilo di vulnerabilità">
      {rilevazioni.length === 0
        ? <p className="tenue piccolo">Nessuna voce rilevata.</p>
        : (
          <Tabella intestazioni={['Voce', 'Rilevata il', 'Conclusa il',
            <span key="comandi" className="solo-lettore">Comandi</span>]}>
            {rilevazioni.map((r) => (
              <tr key={`${r.vulnerabilita}-${giornoIso(r.rilevata_il)}`}>
                <td data-etichetta="Voce">
                  <span className="dato-con-etichetta">
                    {r.classe_dato === 'riservato' && <Etichetta tono="riservato"><IconaLucchetto /> riservata</Etichetta>}
                    {r.nome}
                  </span>
                </td>
                <td data-etichetta="Rilevata il">{giornoLeggibile(r.rilevata_il)}</td>
                <td data-etichetta="Conclusa il">
                  {r.conclusa_il ? giornoLeggibile(r.conclusa_il) : <Etichetta tono="attenzione">aperta</Etichetta>}
                </td>
                <td data-etichetta="Comandi">
                  {scrive(r.classe_dato, true) && (
                    <Bottone variante="secondario" dimensione="piccolo" onClick={() => setInModifica(r)}>
                      {r.conclusa_il ? 'Modifica le date' : 'Concludi o modifica'}
                    </Bottone>
                  )}
                </td>
              </tr>
            ))}
          </Tabella>
        )}

      {aggiungibili.length > 0 && (
        // In stampa il modulo vuoto non serve: resta la tabella delle voci.
        <form ref={radice} noValidate className="persona-non-stampa"
              onSubmit={(e) => { e.preventDefault(); void aggiungi() }}>
          <fieldset className="persona-sottosezione">
            <legend>Nuova voce</legend>
            {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
            <div className="griglia-modulo">
              <Scelta etichetta="Voce" obbligatorio valore={nuova.codice} vuota="— seleziona —"
                      opzioni={aggiungibili.map((v) => ({ valore: v.codice, etichetta: v.nome }))}
                      cambia={(v) => setNuova((n) => ({ ...n, codice: v }))} errore={invio.errori.codice} />
              <Campo tipo="date" etichetta="Data di rilevazione" obbligatorio valore={nuova.rilevata_il} max={oggi}
                     cambia={(v) => setNuova((n) => ({ ...n, rilevata_il: v }))} errore={invio.errori.rilevata_il} />
              <Campo tipo="date" etichetta="Data di conclusione" valore={nuova.conclusa_il} min={nuova.rilevata_il}
                     cambia={(v) => setNuova((n) => ({ ...n, conclusa_il: v }))} errore={invio.errori.conclusa_il}
                     aiuto="Facoltativa: si indica quando la condizione termina." />
            </div>
          </fieldset>
          <div className="persona-comandi">
            <Bottone tipo="submit" variante="primario" inCorso={invio.inCorso}>Aggiungi la voce</Bottone>
          </div>
        </form>
      )}

      {inModifica && (
        <ModaleDateVulnerabilita personaId={scheda.id} rilevazione={inModifica}
                                 chiudi={() => setInModifica(null)}
                                 salvata={async () => { setInModifica(null); await ricarica() }} />
      )}
    </Riquadro>
  )
}

function ModaleDateVulnerabilita({ personaId, rilevazione, chiudi, salvata }: {
  personaId: string; rilevazione: VulnerabilitaLetta; chiudi: () => void; salvata: () => Promise<void>
}) {
  const oggi = oggiLocale()
  const rilevataPrima = giornoIso(rilevazione.rilevata_il)
  const conclusaPrima = giornoIso(rilevazione.conclusa_il)
  const [date, setDate] = useState({ codice: rilevazione.vulnerabilita, rilevata_il: rilevataPrima, conclusa_il: conclusaPrima })
  // Il fuoco entra nella finestra sul primo campo, la data di rilevazione: lo porta Modale.
  const invio = useInvio()

  async function salva() {
    invio.pulisci()
    const verificati = verificaVulnerabilita(date, oggi)
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    const corpo: Record<string, string | null> = {}
    if (date.rilevata_il !== rilevataPrima) corpo.rilevata_il = date.rilevata_il
    if (date.conclusa_il !== conclusaPrima) corpo.conclusa_il = date.conclusa_il || null
    if (!Object.keys(corpo).length) { chiudi(); return }
    invio.setInCorso(true)
    try {
      await api.patch(`/persone/${personaId}/vulnerabilita/${encodeURIComponent(rilevazione.vulnerabilita)}/${rilevataPrima}`, corpo)
      await salvata()
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, (c) => c === 'rilevata_il' || c === 'conclusa_il')
      invio.segnala(errori, errori.rilevata_il || errori.conclusa_il
        ? messaggio : (e instanceof ErroreApi ? e.message : messaggio))
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Date della voce: ${rilevazione.nome}`} chiudi={chiudi}
            azioni={(
              <>
                <Bottone variante="secondario" onClick={chiudi} disabled={invio.inCorso}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={invio.inCorso} onClick={() => void salva()}>Salva</Bottone>
              </>
            )}>
      {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
      <Campo tipo="date" etichetta="Data di rilevazione" obbligatorio valore={date.rilevata_il} max={oggi}
             errore={invio.errori.rilevata_il}
             cambia={(v) => setDate((d) => ({ ...d, rilevata_il: v }))} />
      <Campo tipo="date" etichetta="Data di conclusione" valore={date.conclusa_il} min={date.rilevata_il}
             errore={invio.errori.conclusa_il} cambia={(v) => setDate((d) => ({ ...d, conclusa_il: v }))}
             aiuto="Vuota: la voce resta aperta." />
      {!date.conclusa_il && (
        <div className="riga">
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => setDate((d) => ({ ...d, conclusa_il: oggi }))}>
            Concludi con la data di oggi
          </Bottone>
        </div>
      )}
    </Modale>
  )
}

// --- categoria di utenti (D-21) --------------------------------------------------------------

/**
 * Categorie di utenti: il sistema le propone per eta', genere e tipo di persona, l'operatore le
 * conferma. PUT /persone/:id/categorie decide tutte le voci in gioco insieme: le proposte non
 * spuntate si escludono e non si ripropongono. Per questo le proposte si rivedono in un solo
 * modulo, gia' spuntate, e non con un comando per voce.
 */
function CategorieUtenti({ scheda, ricarica }: { scheda: SchedaLetta; ricarica: Ricarica }) {
  const { cataloghi } = useSessione()
  const catalogo = vociDi(cataloghi, 'categoria_persona')
  const puo = scheda.puo
  const confermate = scheda.categorie.map((c) => c.categoria)
  const proposte = new Map(scheda.categorie_proposte.map((p) => [p.categoria, p.motivo]))
  // Una voce riservata si propone solo a chi la legge sulla scheda, oppure se la scheda gliela
  // mostra gia' (l'ha decisa lui): senza, spuntarla farebbe rispondere l'API 404, e toglierla dal
  // modulo quando e' visibile la escluderebbe.
  const scrivibili = catalogo.filter((v) =>
    scriveCategoria(puo, v.classe_dato, confermate.includes(v.codice) || proposte.has(v.codice)))
  const modificabile = puo.modificare && !scheda.mascherata && scrivibili.length > 0

  const [inModifica, setInModifica] = useState(false)
  const [scelte, setScelte] = useState<string[]>([])
  const invio = useInvio()
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, invio.tentativo)

  const apri = () => {
    const iniziali = new Set([...confermate, ...proposte.keys()])
    setScelte(scrivibili.map((v) => v.codice).filter((c) => iniziali.has(c)))
    invio.pulisci()
    setInModifica(true)
  }

  async function salva() {
    invio.pulisci()
    const eta = etaAllaData(scheda.data_nascita ? giornoIso(scheda.data_nascita) : null, scheda.precisione_nascita, oggiLocale())
    const verificati = verificaCategorie(scelte, confermate, catalogo, eta)
    if (!nessunErrore(verificati)) { invio.segnala(verificati, 'Correggere i campi segnalati.'); return }
    invio.setInCorso(true)
    try {
      await api.put(`/persone/${scheda.id}/categorie`, { confermate: scelte })
      await ricarica()
      setInModifica(false)
    } catch (e) {
      const { errori, messaggio } = erroreSulCampo(e, (c) => c === 'categorie')
      invio.segnala(errori, messaggio)
    } finally {
      invio.setInCorso(false)
    }
  }

  return (
    <Riquadro
      titolo="Categoria di utenti"
      azioni={modificabile && !inModifica
        ? (
          <Bottone variante={proposte.size ? 'primario' : 'secondario'} dimensione="piccolo" onClick={apri}>
            {proposte.size ? 'Rivedi le proposte' : 'Modifica'}
          </Bottone>
        )
        : undefined}
    >
      {!inModifica && (
        <>
          {scheda.categorie.length
            ? (
              <div className="riga-etichette">
                {scheda.categorie.map((c) => <Etichetta key={c.categoria} tono="accento">{c.nome}</Etichetta>)}
              </div>
            )
            : <p className="tenue piccolo">Nessuna categoria confermata.</p>}
          {scheda.categorie_proposte.length > 0 && (
            <>
              <p className="persona-sottotitolo">Proposte dal sistema, da confermare</p>
              <ul className="persona-voce-elenco">
                {scheda.categorie_proposte.map((p) => (
                  <li key={p.categoria}>
                    {p.nome} <span className="tenue piccolo">({MOTIVI_PROPOSTA[p.motivo] ?? p.motivo})</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {inModifica && (
        <form ref={radice} noValidate className="persona-non-stampa"
              onSubmit={(e) => { e.preventDefault(); void salva() }}>
          {invio.errore && <Avviso tipo="errore">{invio.errore}</Avviso>}
          <GruppoCaselle
            legenda="Categorie confermate"
            voci={scrivibili.map((v) => ({
              codice: v.codice, classe_dato: v.classe_dato,
              nome: proposte.has(v.codice) && !confermate.includes(v.codice)
                ? <>{v.nome} <span className="tenue piccolo">— {MOTIVI_PROPOSTA[proposte.get(v.codice)!] ?? 'proposta'}</span></>
                : v.nome,
            }))}
            scelte={scelte} cambia={setScelte} errore={invio.errori.categorie}
            aiuto="Le voci spuntate si confermano. Una proposta non spuntata si esclude e il sistema non la propone più."
          />
          <div className="persona-comandi">
            <Bottone variante="secondario" onClick={() => setInModifica(false)} disabled={invio.inCorso}>Annulla</Bottone>
            <Bottone tipo="submit" variante="primario" inCorso={invio.inCorso}>Conferma le categorie</Bottone>
          </div>
        </form>
      )}
    </Riquadro>
  )
}
